import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Linkedin, Facebook, Instagram, Youtube } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { NewsletterSignup } from "@/components/marketing/NewsletterSignup";
import mtliLogo from "@/assets/mtli-logo.png";

// Footer mirrors the five divisions (same structure as the header nav).
const services = [
  { name: "ICI Construction", nameFr: "Construction ICI", href: "/services/construction-general-contracting" },
  { name: "Storage Solutions & Automation", nameFr: "Solutions d'entreposage et automatisation", href: "/services/racking-automation" },
  { name: "Installation & Maintenance", nameFr: "Installation et maintenance", href: "/services/installations" },
  { name: "Facility Maintenance", nameFr: "Maintenance des installations", href: "/services/facility-maintenance" },
  { name: "Moving & Liquidation", nameFr: "Déménagement et liquidation", href: "/services/relocations" },
  { name: "Electrical & Controls", nameFr: "Électricité et contrôles", href: "/services/electrical-automation-services" },
];

const industries = [
  { name: "Warehousing & Distribution", nameFr: "Entreposage et distribution", href: "/industries/warehousing-distribution" },
  { name: "3PL & Logistics", nameFr: "3PL et logistique", href: "/industries/3pl-logistics" },
  { name: "E-Commerce Fulfillment", nameFr: "Exécution e-commerce", href: "/industries/ecommerce-fulfillment" },
  { name: "Manufacturing", nameFr: "Fabrication", href: "/industries/manufacturing" },
  { name: "Food & Beverage", nameFr: "Alimentation et boissons", href: "/industries/food-beverage" },
  { name: "Cold Storage", nameFr: "Entreposage frigorifique", href: "/industries/cold-storage" },
];

const company = [
  { name: "About MTLI", nameFr: "À propos de MTLI", href: "/about" },
  { name: "Book a Call", nameFr: "Réservez un appel", href: "/book-a-call" },
  { name: "Partner With Us", nameFr: "Devenir partenaire", href: "/partnerships" },
  { name: "Meet the Team", nameFr: "Notre équipe", href: "/about#team" },
  { name: "Projects & Case Studies", nameFr: "Projets et études de cas", href: "/projects" },
  { name: "Safety Program", nameFr: "Programme de sécurité", href: "/about/safety" },
  { name: "Quality Assurance", nameFr: "Assurance qualité", href: "/about/quality" },
  { name: "Certifications", nameFr: "Certifications", href: "/about/certifications" },
  { name: "Careers", nameFr: "Carrières", href: "/careers" },
];

// Canada-specific locations
const canadaLocations = [
  { name: "Ontario", href: "/locations/ontario" },
  { name: "Alberta", href: "/locations/alberta" },
  { name: "British Columbia", nameFr: "Colombie-Britannique", href: "/locations/british-columbia" },
  { name: "Quebec", nameFr: "Québec", href: "/locations/quebec" },
  { name: "All Locations", nameFr: "Tous les emplacements", href: "/locations" },
];

const Footer = () => {
  const { config, isFrench, isUSA, getLocalePath, t } = useI18n();
  
  const locations = canadaLocations;
  
  const getName = (item: { name: string; nameFr?: string }) => 
    isFrench && item.nameFr ? item.nameFr : item.name;

  return (
    <footer className="bg-secondary text-secondary-foreground">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to={getLocalePath("/")} className="inline-block mb-2">
              <img src={mtliLogo} alt="MTLI Group of Companies" className="h-10 w-auto brightness-0 invert" loading="lazy" decoding="async" />
            </Link>
            <p className="text-[10px] uppercase tracking-[0.15em] text-secondary-foreground/50 mb-4 font-medium">
              {isFrench ? "Fabrication • Technologie • Logistique • Infrastructure" : "Manufacturing • Technology • Logistics • Infrastructure"}
            </p>
            <p className="text-sm text-secondary-foreground/80 mb-6">
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              <a href="https://www.linkedin.com/company/mtligroup" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" aria-label="LinkedIn">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="https://www.facebook.com/MTLIGroup" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" aria-label="Facebook">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/mtligroup.co?hl=en" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" aria-label="Instagram">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://www.youtube.com/@MTLIGroup" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" aria-label="YouTube">
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{isFrench ? "Services" : "Services"}</h3>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.name}>
                  <Link to={getLocalePath(service.href)} className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors">
                    {getName(service)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{isFrench ? "Industries" : "Industries"}</h3>
            <ul className="space-y-2">
              {industries.map((industry) => (
                <li key={industry.href}>
                  <Link to={getLocalePath(industry.href)} className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors">
                    {getName(industry)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{isFrench ? "Entreprise" : "Company"}</h3>
            <ul className="space-y-2">
              {company.map((item) => (
                <li key={item.href}>
                  <Link to={getLocalePath(item.href)} className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors">
                    {getName(item)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{isFrench ? "Contact" : "Contact"}</h3>
            <ul className="space-y-3">
              <li>
                <a href={`tel:${config.phone}`} className="flex items-center gap-2 text-sm text-secondary-foreground/80 hover:text-primary transition-colors">
                  <Phone className="h-4 w-4 flex-shrink-0" />
                  {config.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${config.email}`} className="flex items-center gap-2 text-sm text-secondary-foreground/80 hover:text-primary transition-colors">
                  <Mail className="h-4 w-4 flex-shrink-0" />
                  {config.email}
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-secondary-foreground/80">
                <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{t('footer.servingRegion')}</span>
              </li>
            </ul>
            
            <h4 className="font-semibold mt-6 mb-3">{isFrench ? "Emplacements" : "Locations"}</h4>
            <ul className="space-y-2">
              {locations.map((location) => (
                <li key={location.href}>
                  <Link to={getLocalePath(location.href)} className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors">
                    {getName(location)}
                  </Link>
                </li>
              ))}
              <li className="pt-2">
                <a
                  href="https://mtligroup.com/locations"
                  className="text-sm text-secondary-foreground/70 hover:text-primary transition-colors"
                >
                  {isFrench
                    ? "Vous opérez aux États-Unis? Visitez mtligroup.com →"
                    : "Operating in the United States? Visit mtligroup.com →"}
                </a>
              </li>
            </ul>

          </div>
        </div>

        {/* Newsletter Section */}
        <div className="mt-12 pt-8 border-t border-secondary-foreground/20">
          <div className="max-w-xl">
            <NewsletterSignup 
              variant="compact"
              title={isFrench ? "Restez informé" : "Stay Updated"}
              className="bg-secondary-foreground/5 border-secondary-foreground/10 rounded-lg p-4"
            />
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-secondary-foreground/20">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-secondary-foreground/60">
              © {new Date().getFullYear()} MTLI Group. {isFrench ? "Tous droits réservés." : "All rights reserved."}
            </p>
            <div className="flex gap-6">
              <Link to={getLocalePath("/privacy")} className="text-sm text-secondary-foreground/60 hover:text-primary transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to={getLocalePath("/terms")} className="text-sm text-secondary-foreground/60 hover:text-primary transition-colors">
                {t('footer.terms')}
              </Link>
              <Link to="/admin/auth" className="text-sm text-secondary-foreground/60 hover:text-primary transition-colors">
                {isFrench ? "Portail Admin" : "Admin Login"}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
